import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface URLParamConfig<T> {
  key: string
  defaultValue: T
  parser?: (value: string) => T
}

/**
 * Custom hook for managing URL parameters with type safety and automatic synchronization
 */
export const useURLParams = <T extends Record<string, unknown>>(configs: {
  [K in keyof T]: URLParamConfig<T[K]>
}) => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse current values from URL
  const values = useMemo(() => {
    const result = {} as T

    for (const [key, config] of Object.entries(configs)) {
      const urlValue = searchParams.get(config.key)

      if (urlValue !== null) {
        if (config.parser) {
          result[key as keyof T] = config.parser(urlValue)
        } else {
          // Default parsing based on type
          if (typeof config.defaultValue === 'number') {
            result[key as keyof T] = parseInt(urlValue, 10) as T[keyof T]
          } else {
            result[key as keyof T] = urlValue as T[keyof T]
          }
        }
      } else {
        result[key as keyof T] = config.defaultValue
      }
    }

    return result
  }, [searchParams, configs])

  // Update a single parameter
  const updateParam = useCallback(
    <K extends keyof T>(key: K, value: T[K], resetPage: boolean = true) => {
      const newSearchParams = new URLSearchParams(searchParams)
      const config = configs[key]

      if (value === config.defaultValue || (typeof value === 'string' && value === '')) {
        newSearchParams.delete(config.key)
      } else {
        newSearchParams.set(config.key, String(value))
      }

      // Reset page to 1 when other parameters change
      if (resetPage && key !== 'page' && configs.page) {
        newSearchParams.set(configs.page.key, '1')
      }

      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams, configs],
  )

  // Update multiple parameters at once
  const updateParams = useCallback(
    (updates: Partial<T>, resetPage: boolean = true) => {
      const newSearchParams = new URLSearchParams(searchParams)

      for (const [key, value] of Object.entries(updates)) {
        const config = configs[key as keyof T]

        if (!config) continue

        if (value === config.defaultValue || (typeof value === 'string' && value === '')) {
          newSearchParams.delete(config.key)
        } else {
          newSearchParams.set(config.key, String(value))
        }
      }

      // Reset page to 1 when other parameters change
      if (resetPage && !updates.page && configs.page) {
        newSearchParams.set(configs.page.key, '1')
      }

      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams, configs],
  )

  // Clear only managed parameters (preserve other query params)
  const clearParams = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams)

    // Only delete parameters that are managed by this hook
    for (const config of Object.values(configs)) {
      newSearchParams.delete(config.key)
    }

    setSearchParams(newSearchParams, { replace: true })
  }, [searchParams, setSearchParams, configs])

  return {
    values,
    updateParam,
    updateParams,
    clearParams,
  }
}
